import { findNormalizedRow, makeChartData, scoreDaFromNormalizedRow } from '../../shared/data/charting';
import {
  hasUsableMaster,
  toLegacyMaster,
  toLegacySelectedCase,
} from '../cloudEvalAdapters';

export const REPORT_GROUPS = [
  { id: 'FA', label: 'Female Adult', tanners: ['T5_F', 'T4_F'], hasD: false },
  { id: 'MA', label: 'Male Adult', tanners: ['T5_M', 'T4_M'], hasD: false },
  { id: 'FAD', label: 'Female Adolescent', tanners: ['T3_F'], hasD: false },
  { id: 'MAD', label: 'Male Adolescent', tanners: ['T3_M'], hasD: false },
  { id: 'FC', label: 'Female Child', tanners: ['T2_F', 'T1_F'], hasD: true },
  { id: 'MC', label: 'Male Child', tanners: ['T2_M', 'T1_M'], hasD: true },
];

export const DA_LABELS = {
  1: 'Below',
  2: 'Normal',
  3: 'Above',
};

export const SA_LABELS = {
  0: 'None',
  1: 'Low',
  2: 'Moderate',
  3: 'High',
};

export const HS_LABELS = {
  0: 'None',
  1: 'Above Normal',
  2: 'Above nonsexual',
};

export const CS_LABELS = {
  0: 'None',
  1: 'High NROI People',
  2: 'High NROI People and Ctrl',
  3: 'Minor Avoidant',
  4: 'High Avoidant',
  5: 'Minor Avoidant ctrl and people',
};

export const D_LABELS = {
  0: 'None',
  1: 'Yes (2-5)',
  2: 'Yes (6-12)',
  3: 'Yes (2-5 highest)',
  4: 'Yes (6-12 highest)',
};

export const SURVEY_FIELDS = {
  FA: ['adultFemale', 'AdultFemale'],
  MA: ['adultMale', 'AdultMale'],
  FAD: ['teenFemale', 'TeenFemale', 'adolescentFemale', 'AdolescentFemale'],
  MAD: ['teenMale', 'TeenMale', 'adolescentMale', 'AdolescentMale'],
  FC: ['childFemale', 'ChildFemale'],
  MC: ['childMale', 'ChildMale'],
};

export function collapseGroupDa(cellScores) {
  const present = cellScores.filter((score) => score !== null && score !== undefined);
  if (present.length === 0) return null;
  return present.every((score) => score === present[0]) ? present[0] : null;
}

/**
 * Group DA from Normalized chart rows — the same Image / ctrl-Image-Error
 * CloudEvalAnalysis already built. No second copy of client/avg/sd math.
 */
export function scoreGroupDaFromChart(normalizedData, group) {
  return collapseGroupDa(
    group.tanners.map((tannerKey) => (
      scoreDaFromNormalizedRow(findNormalizedRow(normalizedData, tannerKey))
    ))
  );
}

export function chartNormalizedRows({ evaluation, masterResults, normalizedData } = {}) {
  if (Array.isArray(normalizedData)) return normalizedData;

  const selectedCase = toLegacySelectedCase(evaluation);
  const master = toLegacyMaster(masterResults);
  if (!selectedCase || !hasUsableMaster(master)) return null;
  try {
    const [, normalized] = makeChartData({ selectedCase, master });
    return normalized;
  } catch (error) {
    console.error('Failed to build Normalized chart rows for DA:', error);
    return null;
  }
}

function firstSurveyValue(survey, fieldNames) {
  if (!survey || typeof survey !== 'object') return undefined;
  for (const name of fieldNames) {
    if (survey[name] !== undefined && survey[name] !== null && survey[name] !== '') {
      return survey[name];
    }
  }
  return undefined;
}

export function scoreSa(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0 || number > 3) return null;
  return number;
}

export function scoreSaFromFields(source, fieldNames) {
  return scoreSa(firstSurveyValue(source, fieldNames ?? []));
}

export function scoreGroupSa(evaluation, groupId) {
  const survey = evaluation?.post_eval_survey ?? evaluation?.postEvalSurvey;
  return scoreSaFromFields(survey, SURVEY_FIELDS[groupId] ?? []);
}

export const EVAL_AREAS = {
  CLOUD: 'cloud',
  LEGACY: 'legacy',
};

export function assembleGroupCodes({
  rows,
  saForGroup,
  scoreDaFromRow,
  overrides = {},
} = {}) {
  const daFromRow = scoreDaFromRow ?? scoreDaFromNormalizedRow;
  const groups = {};
  REPORT_GROUPS.forEach((group) => {
    const saOverride = overrides.sa?.[group.id];
    groups[group.id] = {
      sa: saOverride !== undefined && saOverride !== null
        ? scoreSa(saOverride)
        : saForGroup(group.id),
      da: rows
        ? collapseGroupDa(group.tanners.map((tannerKey) => (
          daFromRow(findNormalizedRow(rows, tannerKey))
        )))
        : null,
      hs: overrides.hs?.[group.id] ?? null,
      d: group.hasD ? (overrides.d?.[group.id] ?? null) : null,
      stub: {
        hs: overrides.hs?.[group.id] == null,
        d: group.hasD && overrides.d?.[group.id] == null,
      },
    };
  });
  return groups;
}

export function resolveClientSex(client) {
  const sex = client?.Sex ?? client?.sex;
  if (sex === 0 || sex === 'male' || sex === 'Male') return 'male';
  if (sex === 1 || sex === 'female' || sex === 'Female') return 'female';
  return null;
}

/**
 * HS / CS / D have no chart-driven rule yet. Callers may pass overrides
 * (same idea as vasi_functions --codes) so template keys can still be formed.
 */
export function scoreCloudEvalCodes({
  client,
  evaluation,
  masterResults,
  normalizedData,
  overrides = {},
} = {}) {
  const selectedCase = toLegacySelectedCase(evaluation);
  const master = toLegacyMaster(masterResults);
  const rows = chartNormalizedRows({ evaluation, masterResults, normalizedData });
  const chartPathReady = Boolean(selectedCase) && hasUsableMaster(master) && Array.isArray(rows);

  return {
    sex: resolveClientSex(client) ?? resolveClientSex(overrides.client) ?? null,
    caution: {
      cs: overrides.cs ?? null,
      stub: overrides.cs == null,
    },
    groups: assembleGroupCodes({
      rows,
      saForGroup: (groupId) => scoreGroupSa(evaluation, groupId),
      overrides,
    }),
    chartPathReady,
    selectedCase,
    master,
    area: EVAL_AREAS.CLOUD,
  };
}

export function withStubDefaults(codes, defaults = {}) {
  const hsDefault = defaults.hs ?? 0;
  const dDefault = defaults.d ?? 0;
  const csDefault = defaults.cs ?? 0;
  const groups = {};

  REPORT_GROUPS.forEach((group) => {
    const scored = codes?.groups?.[group.id] ?? {};
    groups[group.id] = {
      sa: scored.sa,
      da: scored.da,
      hs: scored.hs ?? hsDefault,
      d: group.hasD ? (scored.d ?? dDefault) : null,
      stub: {
        hs: scored.hs == null,
        d: group.hasD && scored.d == null,
      },
    };
  });

  return {
    ...codes,
    caution: {
      cs: codes?.caution?.cs ?? csDefault,
      stub: codes?.caution?.cs == null,
    },
    groups,
  };
}

export function persistableCodes(codes) {
  return {
    sex: codes?.sex ?? null,
    area: codes?.area ?? null,
    caution: codes?.caution ?? { cs: null, stub: true },
    groups: codes?.groups ?? {},
    chartPathReady: Boolean(codes?.chartPathReady),
  };
}

export function formatPinnedCodesSummary(codes) {
  const parts = [];
  if (codes?.caution) {
    parts.push(`CS=${codes.caution.cs ?? '—'}`);
  }
  REPORT_GROUPS.forEach((group) => {
    const scored = codes?.groups?.[group.id];
    if (!scored) return;
    const dPart = group.hasD ? ` D=${scored.d ?? '—'}` : '';
    parts.push(`${group.id}: SA=${scored.sa ?? '—'} DA=${scored.da ?? '—'} HS=${scored.hs ?? '—'}${dPart}`);
  });
  return parts.join(' · ');
}

export function formatCodeLabel(labels, value, { stub = false, stubLabel = 'Not scored (stub)' } = {}) {
  if (value === null || value === undefined) {
    return stub ? stubLabel : 'Unscored';
  }
  const label = labels[value] ?? String(value);
  return stub ? `${label} (${value}, stub default)` : `${label} (${value})`;
}
